import { apiClient } from "@/lib/api";
import { getRoutes } from "@/api/routes";
import type { RouteStatusCounts, RouteSums } from "@/types/routes";
import { yesterdayOf } from "@/lib/date";

/** Shape returned by GET /api/v1/reports/dashboard/ */
interface ApiDashboardResponse {
  total_orders: number;
  total_routes: number;
  otd_percent: number | null;
  late_percent: number | null;
  unvisited_percent: number | null;
  avg_deviation_min: number | null;
  avg_time_per_order_planned_min: number | null;
  avg_time_per_order_actual_min: number | null;
  total_planned_mileage_km: string | null;
  total_route_cost: string | null;
  stop_count_for_billing: number;
}

export interface DashboardStats {
  total_routes: number;
  active_vehicles: number;
  orders_delivered: number;
  /** null when the backend does not provide on-time data; display as "N/A" */
  on_time_rate: number | null;
  /**
   * BUG-528: routes ARCHIVED on the day, over the whole day (server-side).
   *
   * This is deliberately NOT labelled «Не доставлено» any more. It used to be,
   * and it was simply untrue: archiving is a housekeeping action, and real
   * delivery failures are the STOP statuses `rejected` + `skipped`, which no
   * whole-day aggregate exposes today (see `stops_undelivered` below). Rather
   * than approximate a failure count from route housekeeping, the card now says
   * what the number actually is.
   */
  routes_archived: number;
  /**
   * BUG-528: stops closed WITHOUT delivery (`rejected` + `skipped`) across the
   * whole day — the real "not delivered" figure, matching the definition of the
   * route serializer's `stops_count_undelivered` (TASK-4901). `confirmed` is a
   * successful delivery and is never counted.
   *
   * Now a genuine SERVER-SIDE whole-day aggregate: `sums.undelivered` on the
   * routes list, computed before pagination. It is a JSON number (a `Count`),
   * NOT a decimal string like `sums.distance` — no `parseFloat`.
   *
   * Stays `number | null` because the value is still absent while the request
   * is in flight or if `sums` is missing from an older cached payload; `null`
   * renders as «—», never as a confident `0`. A real zero day returns `0`.
   */
  stops_undelivered: number | null;
  /** Whole-day per-status route counts (server-side, pre-pagination). */
  status_counts?: RouteStatusCounts;
  /** Whole-day stop/distance aggregates (server-side, pre-pagination). */
  sums?: RouteSums;
  /** Yesterday's values — omitted when yesterday data is unavailable */
  prev_total_routes?: number;
  prev_orders_delivered?: number;
}

/**
 * Fetches dashboard KPI stats for a given day.
 *
 * BUG-527: every route-derived figure here is now a SERVER-SIDE aggregate. The
 * previous version summed one 20-row DRF page (`page_size = 20`,
 * `apps/core/pagination.py`), so «Активных ТС», «Доставлено» and «Не доставлено»
 * silently understated any day with 21+ routes — sitting beside «Маршрутов
 * сегодня», which was already a correct server count. The fix is aggregation,
 * not a bigger page: raising `page_size` only moves the threshold.
 *
 * What each field reads, and why:
 * - `total_routes`     → reports dashboard endpoint (already server-wide).
 * - `active_vehicles`  → `status_counts.active` from the routes list, computed
 *                        over the whole filtered queryset before pagination.
 * - `orders_delivered` → `sums.stops` from a `status=completed`-SCOPED list
 *                        request. Unlike `status_counts`, `sums` DOES respect
 *                        the `status` filter, so this is the whole day's stop
 *                        total on completed routes. `page_size=1` is sent
 *                        because only the aggregate is wanted, never the rows.
 * - `routes_archived`  → `status_counts.archived` (see BUG-528 on the label).
 * - `stops_undelivered`→ `sums.undelivered` from the SAME unscoped whole-day
 *                        list request that feeds `sums.stops`. Deliberately the
 *                        unfiltered one: undelivered stops live on routes of
 *                        several statuses (in dev, all 3 on 2026-05-25 sit on
 *                        `archived` routes), so scoping it to one status would
 *                        undercount.
 *
 * @param dateISO The day to report on, ISO `YYYY-MM-DD`. Supplied by the caller
 *   so it is the TENANT's today (BUG-529), not a UTC slice of the browser clock.
 */
export async function getDashboardStats(
  dateISO: string,
): Promise<DashboardStats> {
  const today = dateISO;
  const yesterday = yesterdayOf(dateISO);

  // One aggregate-only list request per figure. `page_size: 1` keeps the row
  // payload at a single route while `status_counts`/`sums` still cover the whole
  // day — they are computed before pagination.
  const [reportResp, allRoutes, completedRoutes, yestResp] = await Promise.all([
    apiClient
      .get<ApiDashboardResponse>("/v1/reports/dashboard/", {
        params: { date_from: today, date_to: today },
      })
      .catch(() => null),
    getRoutes({ date: today, page_size: 1 }),
    getRoutes({ date: today, status: "completed", page_size: 1 }),
    apiClient
      .get<ApiDashboardResponse>("/v1/reports/dashboard/", {
        params: { date_from: yesterday, date_to: yesterday },
      })
      .catch(() => null),
  ]);

  const statusCounts = allRoutes.status_counts;
  const sums = allRoutes.sums;

  // Build yesterday comparison fields only when the request succeeded.
  // prev_orders_delivered uses total_orders (closest API proxy for delivered orders count).
  //
  // BUG-528: there is deliberately no `prev_failed_deliveries`. It used to be
  // `max(0, prev_total_routes - prev_total_orders)`, an "approximate" expression
  // whose two terms are routes and stops — subtracting one from the other is not
  // an approximation of anything. The archived card carries no delta rather than
  // a fabricated one.
  const prevFields =
    yestResp !== null
      ? {
          prev_total_routes: yestResp.data.total_routes,
          prev_orders_delivered: yestResp.data.total_orders,
        }
      : {};

  return {
    // `count` is the same whole-day route total the reports endpoint returns;
    // prefer the reports value when present so the card is unchanged, and fall
    // back to the list `count` (also server-side) when that request failed.
    total_routes: reportResp?.data.total_routes ?? allRoutes.count,
    active_vehicles: statusCounts?.active ?? 0,
    orders_delivered: completedRoutes.sums?.stops ?? 0,
    on_time_rate: reportResp?.data.otd_percent ?? null,
    routes_archived: statusCounts?.archived ?? 0,
    // BUG-528: the whole-day rejected+skipped aggregate now exists server-side,
    // so this reads it directly instead of rendering «—». `?? null` (not `?? 0`)
    // keeps a MISSING `sums` distinguishable from a real zero day: the backend
    // always sends the key, so `null` here means "no aggregate came back", which
    // must not be shown as a confident "nothing failed".
    stops_undelivered: sums?.undelivered ?? null,
    status_counts: statusCounts,
    sums,
    ...prevFields,
  };
}
